import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

export interface CronRun {
  id: number;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number | null;
  summary: Record<string, any> | null;
  error: string | null;
  triggeredBy: string | null;
}

export interface CronTask {
  key: string;
  label: string;
  schedule: string;
  latest: CronRun | null;
  runs: CronRun[];
}

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser;
  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;
    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) unsubscribe();
      resolve(null);
    }, timeoutMs);
    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function getCronStatus(): Promise<CronTask[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/cron-status`, { headers });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch cron status');
  }
  const data = await response.json();
  return data.tasks;
}

const TRIGGER_ENDPOINTS: Record<string, string> = {
  exposure_calc: '/api/v2/exposure/calculate-all',
  volatility_calc: '/api/v2/risk/calculate-volatility',
  strategy_eval: '/api/v2/watch/evaluate-all',
  account_snapshot: '/api/v2/snapshots/capture-all',
};

export function canTrigger(taskKey: string): boolean {
  return taskKey in TRIGGER_ENDPOINTS;
}

export interface PipelineRun {
  id: number;
  accountId: number;
  accountName: string | null;
  trigger: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  steps: {
    exposure: boolean | null;
    derivation: boolean | null;
    eval: boolean | null;
  };
  notificationsCreated: number | null;
  error: string | null;
  notes: string | null;
  log: Array<{ step: string; ok: boolean; durationMs: number; detail?: string }>;
}

export async function getPipelineRuns(accountId?: number, limit = 50): Promise<PipelineRun[]> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ limit: String(limit) });
  if (accountId) params.set('accountId', String(accountId));
  const response = await fetch(`${API_URL}/api/v2/cron-status/pipeline-runs?${params}`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch pipeline runs');
  }
  const data = await response.json();
  return data.runs;
}

export async function triggerPipelineRun(accountId: number): Promise<{ message: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/pipeline/run/${accountId}`, { method: 'POST', headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to trigger pipeline');
  }
  return response.json();
}

export interface AccountOption {
  id: number;
  name: string;
}

export async function getAccountOptions(): Promise<AccountOption[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/v2/accounts`, { headers });
  if (!response.ok) throw new Error('Failed to fetch accounts');
  const data = await response.json();
  return (data.accounts ?? data ?? []).map((a: any) => ({ id: Number(a.id), name: a.name }));
}

export async function triggerCronTask(taskKey: string): Promise<any> {
  const endpoint = TRIGGER_ENDPOINTS[taskKey];
  if (!endpoint) throw new Error(`Task ${taskKey} cannot be triggered manually`);
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}${endpoint}`, { method: 'POST', headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to trigger ${taskKey}`);
  }
  return response.json();
}
